


































































































































































import { Vue, Component, Prop, Watch } from "@/shared/vuepack";
import Button from "@/shared/components/ui/Button.vue";
import { useThemeStore } from "@/shared/store/theme";
import { AuthorizedSigner, Address } from "@/shared/types";
import UsStateSelector from "@/shared/components/UsStateSelector.vue";
import { useAuthorizedSignerStore } from "@/employee/store/authorizedSigner";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import _ from "lodash";
import * as Ssn from "@/shared/ssn";
import AddressForm from "@/employee/components/settings/profile/AddressForm.vue";
import { useAddressFormStore } from "@/employee/store/addressForm";

const emptyAddress: Address = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: ""
};

@Component({
  components: {
    Button,
    UsStateSelector,
    GenericLoadingSpinner,
    AddressForm
  }
})
export default class EditAuthorizedSigner extends Vue {
  themeStore = useThemeStore();
  authorizedSignerStore = useAuthorizedSignerStore();
  addressFormStore = useAddressFormStore();

  isSubmitting = false;
  addressModel = _.cloneDeep(emptyAddress);
  isModalScrollable = false;

  @Prop({ required: true })
  authorizedSigner: AuthorizedSigner;

  @Prop({ required: true })
  onSave: () => Promise<void>;

  @Prop()
  toggleClose: () => void;

  @Prop()
  isNew: boolean;

  @Prop()
  show: { value: boolean };

  @Prop({ required: false, default: false })
  isLoading: boolean;

  @Prop({ required: false, default: false })
  disableSubmit: boolean;

  @Prop({ required: false })
  readonlyProps: [];

  @Watch("authorizedSigner")
  onAuthorizedSignerChanged () {
    this.populateAddressModelFromAuthorizedSigner();
  }

  get title () {
    return this.isNew ? "Add Authorized Signer" : "Update Authorized Signer";
  }

  get submitDisabled () {
    return !this.isValid || this.isSubmitting || this.isLoading || this.disableSubmit || this.addressFormStore.saveDisabled;
  }

  get isValid () {
    return !this.$validator.errors.any() && this.addressFormStore.isValid;
  }

  get isEligibleForDebitCard () {
    return this.authorizedSignerStore.isEligibleForDebitCard;
  }

  populateAddressModelFromAuthorizedSigner () {
    if (this.authorizedSigner) {
      this.addressModel = {
        addressLine1: this.authorizedSigner.address1,
        addressLine2: this.authorizedSigner.address2,
        city: this.authorizedSigner.city,
        state: this.authorizedSigner.state,
        zip: this.authorizedSigner.zip
      };
    } else {
      this.addressModel = _.cloneDeep(emptyAddress);
    }
  }

  save () {
    this.copyAddressModelToAuthorizedSigner();

    this.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.verifyAddress()
        .then(() => {
          this.isSubmitting = true;

          this.onSave().finally(() => {
            this.isSubmitting = false;
          });
        })
        .catch(() => {/*noop*/});
    });
  }

  verifyAddress (): Promise<void> {
    const addressForm = this.getAddressComponent();

    if (addressForm) {
      return addressForm.verifyAddress();
    }

    return Promise.resolve();
  }

  copyAddressModelToAuthorizedSigner () {
    this.authorizedSigner.address1 = this.addressModel.addressLine1;
    this.authorizedSigner.address2 = this.addressModel.addressLine2;
    this.authorizedSigner.city = this.addressModel.city;
    this.authorizedSigner.state = this.addressModel.state;
    this.authorizedSigner.zip = this.addressModel.zip;
  }

  validateAll () {
    const validations = [
      this.$validator.validateAll(),
      this.validateAddress()
    ];

    return Promise.all(validations).then(results => {
      return results.every(result => result === true);
    });
  }

  validateAddress (): Promise<boolean> {
    const addressForm = this.getAddressComponent();

    if (addressForm) {
      return addressForm.validate();
    }

    return Promise.resolve(true);
  }

  isDisabled (field): boolean {
    if (!this.readonlyProps || this.readonlyProps.length < 1) {
      return false;
    }

    return this.readonlyProps.findIndex(x => x === field) >= 0;
  }

  ssnMasked (): string {
    if (!this.authorizedSigner.ssn) {
      return "";
    }
    return Ssn.lastFour(this.authorizedSigner.ssn);
  }

  onSsnChange (e) {
    this.$emit("ssnChanged", e);
  }

  onClose () {
    this.addressFormStore.resetState();
    this.toggleClose();
  }

  getAddressComponent () {
    return this.$refs.addressForm as any;
  }

  mounted () {
    this.setModalScrollable();
    window.addEventListener("resize", this.setModalScrollable);
  }

  setModalScrollable () {
    const smallScreenMaxBreakpoint = 768;
    this.isModalScrollable = window.innerWidth < smallScreenMaxBreakpoint;
  }

  beforeDestroy () {
    window.removeEventListener("resize", this.setModalScrollable);
  }
}
